import { View } from '@smarterservices/smarter-ui';
import React from 'react';
import { useAppStore } from '../../../stores/app';
import Loader from '../../Common/Loader';
import NamedMediaTrack from '@ui/shared/components/Common/NamedMediaTrack';

/**
 * SecondCameraVideo Component
 * 
 * Displays the live video stream from a remote second camera participant in a proctoring session.
 * This component is used during the second camera connection workflow to show the video feed
 * from a test-taker's mobile device or secondary camera.
 * 
 * ## Features
 * - **Automatic Track Subscription**: Connects to the "secondCamera" track from the remote participant
 * - **Smart Video Sizing**: Automatically adjusts video dimensions based on orientation (portrait/landscape)
 * - **Visual Styling**: Applies rounded corners and shadow for polished appearance
 * - **Loading States**: Shows loader while waiting for video stream to initialize
 * - **Timeout Handling**: Triggers callback if video fails to load within specified time
 * - **State Monitoring**: Logs track state changes for debugging
 * 
 * ## Store Dependencies
 * - `mediaAgent`: LiveKit room connection manager from app store
 * - `secondCameraIdentity`: Unique identifier for the second camera participant
 * 
 * ## Video Container
 * - Fixed height: 40vh (40% of viewport height)
 * - Full width: 100%
 * - Centered content with relative positioning
 * 
 * ## Track Configuration
 * - **Track Name**: "secondCamera" - matches the track published by the mobile device
 * - **Border Radius**: Small rounded corners for modern appearance
 * - **Shadow**: Elevated shadow effect for depth
 * - **Orientation Detection**: Automatically detects portrait/landscape and adjusts sizing
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} [props.onVideoLoaded] - Callback fired when video track starts playing successfully.
 *                                           Called with the LiveKit Track object.
 * @param {Function} [props.onVideoTimeout] - Callback fired when video loading exceeds the timeout period.
 *                                            Use this to handle connection failures or show error messages.
 * @param {number} [props.loadTimeout=10] - Maximum time in seconds to wait for video before timing out.
 *                                          Default is 10 seconds.
 * 
 * @returns {React.ReactElement|null} Video display component with loading states, or null if dependencies unavailable
 * 
 * @example
 * // Basic usage with default timeout
 * <SecondCameraVideo />
 * 
 * @example
 * // With custom callbacks and timeout
 * <SecondCameraVideo
 *   onVideoLoaded={(track) => console.log('Video ready:', track)}
 *   onVideoTimeout={() => showErrorMessage('Video connection failed')}
 *   loadTimeout={15}
 * />
 * 
 * @example
 * // In a camera connection workflow
 * function CameraConnection() {
 *   const [videoReady, setVideoReady] = useState(false);
 *   
 *   return (
 *     <div>
 *       <h2>Second Camera Preview</h2>
 *       <SecondCameraVideo
 *         onVideoLoaded={() => setVideoReady(true)}
 *         onVideoTimeout={() => alert('Camera connection failed')}
 *       />
 *       {videoReady && <Button>Continue</Button>}
 *     </div>
 *   );
 * }
 */
const SecondCameraVideo = ({
    onVideoLoaded = () => { },
    onVideoTimeout = () => { },
    loadTimeout = 10,
}) => {
    // Get required LiveKit connection and participant identity from global store
    const mediaAgent = useAppStore((state) => state.mediaAgent);
    const secondCameraIdentity = useAppStore((state) => state.secondCameraIdentity);

    // Early return if required dependencies are not available
    // This prevents rendering errors when store hasn't initialized
    if (!mediaAgent || !secondCameraIdentity) {
        return null;
    }

    // Render video display with loading state and error handling
    return (
        <View
            as="div"
            className="second-camera-wrapper"
            position="relative"
            textAlign="center"
            width="100%"
            height="40vh" // 40% of viewport height for consistent sizing
        >
            {/* 
                NamedMediaTrack: Bulletproof LiveKit track renderer
                - Handles subscription, retries, and error recovery automatically
                - Detects video orientation and applies smart sizing
                - Provides loading states and timeout handling
            */}
            <NamedMediaTrack
                room={mediaAgent.getRoom()} // LiveKit room instance
                identity={secondCameraIdentity} // Participant identity to find
                trackName="secondCamera" // Specific track name to subscribe to
                videoBorderRadius="small" // Rounded corners (0.25rem)
                videoShadow="above" // Elevated shadow effect

                // Custom loading UI with timeout support
                renderLoading={(state, data) => (
                    <Loader
                        text="Waiting for video..."
                        onTimeout={onVideoTimeout}
                        timeout={loadTimeout}
                    />
                )}

                // Debug logging for track state transitions
                onStateChange={(state, data) => {
                    console.log('NamedMediaTrack state change:', state, data);
                }}

                // Callback when video actually starts playing (not just ready)
                onTrackPlaying={onVideoLoaded}
            />
        </View>
    );
};

export default SecondCameraVideo;
